import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { ListItem } from 'src/app/shared/models/list-item.model';
import {
    DeclineReasons,
    GridLookups,
    OfferDetailsRecruiting,
    VendorOptions,
    OfferNotes,
    CandidateOfferClientNote,
    OfferType,
    GridDataItems
} from 'src/app/shared/models/offers.model';
import { OfferMarginDifference } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/model/offer-margin-difference.model';
import { appendQuery, combine, getQueryString } from 'src/app/shared/utilities/route';

@Injectable({
    providedIn: 'root'
})
export class RecruitingDealsService {
    private readonly coreApiPath: string;
    private readonly recruitingPath: string;
    private readonly attachmentsPath: string;
    private readonly offerTypeEnum = OfferType;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private readonly http: HttpClient
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/');
        this.recruitingPath = combine(this.coreApiPath, 'candidate-offer/recruiting');
        this.attachmentsPath = combine(this.coreApiPath, 'candidate-offer/attachments');
    }

    getGridData(args: FetchArgs): Observable<PagingToken<GridDataItems[]>> {
        const url = this.recruitingPath;
        const params = this.cleanupArgs(args);

        return this.getPaged(args.pageSize, args.skip, url, params);
    }

    lookups(): Observable<GridLookups> {
        const url = `${this.recruitingPath}/lookups`;
        return this.http.get<GridLookups>(url);
    }

    getOffer(id: number): Observable<OfferDetailsRecruiting> {
        const url = `${this.recruitingPath}/${id}`;
        return this.http.get<OfferDetailsRecruiting>(url);
    }

    getOfferMarginDifferences(offerId: number): Observable<OfferMarginDifference[]> {
        const url = `${this.recruitingPath}/${offerId}/margin-differences`;
        return this.http.get<OfferMarginDifference[]>(url);
    }

    getDeclineReasons(): Observable<DeclineReasons[]> {
        const url = `${this.recruitingPath}/decline/reasons`;
        return this.http.get<DeclineReasons[]>(url);
    }

    getVendorOptions(nameSearch: string): Observable<VendorOptions[]> {
        const url = `${this.recruitingPath}/vendors?vendorName=${nameSearch}`;
        return this.http.get<VendorOptions[]>(url);
    }

    getFacilityOptions(nameSearch: string): Observable<ListItem[]> {
        const url = `${this.recruitingPath}/facilities?facilityName=${nameSearch}`;
        return this.http.get<ListItem[]>(url);
    }

    putDeclineOffer(offerId: number, data: any) {
        const url = `${this.recruitingPath}/${offerId}/decline`;
        return this.http.put<any>(url, data);
    }

    getOfferNotes(id: number): Observable<OfferNotes[]> {
        const url = `${this.recruitingPath}/${id}/notes`;
        return this.http.get<OfferNotes[]>(url);
    }

    postOfferNoteAndNotifyAM(id: number, note: CandidateOfferClientNote): Observable<CandidateOfferClientNote> {
        const url = `${this.recruitingPath}/${id}/note-and-notify`;
        return this.http.post<CandidateOfferClientNote>(url, note);
    }

    getDealsExport(request: FetchArgs): Observable<ArrayBuffer> {
        const params = this.cleanupArgs(request);
        let url = `${this.recruitingPath}/export`;
        const filterObj = Object.assign({}, params);
        filterObj['offerType'] = this.offerTypeEnum.Direct;
        if (filterObj) {
            url += getQueryString(filterObj as CompositeFilterDescriptor);
        }
        if (request.sort && request.sort.field && request.sort.field.length) {
            url = appendQuery(url, `sortField=${request.sort.field}&sortType=${request.sort.dir || 'asc'}`);
        }
        const path = appendQuery(url, `pageSize=${request.pageSize}`, `skip=${request.skip}`);
        return this.http.get(path, { responseType: 'arraybuffer' });
    }

    cleanupArgs(args: FetchArgs) {
        const params = {};

        if (args.filter) {
            for (const prop in args.filter) {
                let filterName: string;
                const val = args.filter[prop];
                switch (prop) {
                    case 'profession':
                        filterName = 'professionId';
                        break;
                    case 'offerStatus':
                        filterName = 'offerStatusIds';
                        break;
                    case 'facilityId':
                        filterName = 'facilityName';
                        break;
                    default:
                        filterName = prop;
                }
                params[filterName] = val;
            }
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            let filterName: string;
            switch (args.sort.field) {
                case 'profession':
                    filterName = 'professionId';
                    break;
                case 'expertise':
                    filterName = 'expertiseId';
                    break;
                case 'offerStatus':
                    filterName = 'offerStatusId';
                    break;
                case 'contractType':
                    filterName = 'contractTypeId';
                    break;
                default:
                    filterName = args.sort.field;
            }
            params['sortField'] = filterName;
            params['sortType'] = args.sort.dir || 'asc';
        }

        return params;
    }

    protected getPaged<T>(pageSize: number, skip: number, url: string, params: any): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<T[]>>(path, { params });
    }
}
